import { useEffect, useRef, useState } from 'react';
import { EMAIL_SENT_MARKER_PREFIX } from '@/lib/emailCacheKeys';
import type { EmailDraftCardData } from './types';

/**
 * Editable email result card.
 *
 * The card arrives with the recipient pre-filled and the user's rough note in
 * the body. Two actions:
 *  - ✨ Xavior Rewrite — rewrites the rough note into a complete email (subject
 *    + body), ALTERNATING between a professional and a casual tone on each click
 *    and diverging from the previous draft, so clicking again gives a fresh
 *    take. onCompose() does the generation (parent fetch) and returns the draft.
 *  - Send — sends the email. onSend() does the actual POST (parent) and returns
 *    success so the card can lock on send (no double-send of a real email).
 *
 * Each rewrite re-rewrites the ORIGINAL note (`seed`), not the already-polished
 * body, so iterations stay anchored to the user's intent instead of drifting.
 *
 * ## Reply auto-pre-draft (Bug #4747)
 *
 * In REPLY mode (`data.replyMode` / a `threadId`) the agent's email_compose tool
 * emits the card with an EMPTY body — it makes no LLM call itself. Without help
 * the user faced a blank reply card and had to click Rewrite to populate it. The
 * card now auto-fires the SAME draft-reply path Rewrite uses (`onCompose`) ONCE
 * on mount when reply mode lands with no seeded body, so the reply arrives
 * pre-drafted and editable. The non-reply compose path is unchanged (it still
 * waits for the user's rough note + an explicit Rewrite click).
 *
 * Mirrors the bordered card chrome of EmailResultCard (rc-card / rc-meta).
 *
 * ## Sent-state persistence (Bug #4675)
 *
 * `sendState` is lifted into a module-level registry keyed by a stable cardKey
 * (messageId + to|subject|threadId). This survives React re-mounts that would
 * otherwise reset ephemeral useState back to 'idle', re-exposing the Send
 * button after the email was already dispatched.
 *
 * The parent derives the same cardKey and passes it plus a `sent` boolean
 * (initial value from the registry) and an `onSent` callback. The card calls
 * `onSent(cardKey)` on successful send so the parent can record it — and the
 * registry entry is also written here for resilience.
 *
 * ## Durable sent-state (Bug #4982)
 *
 * The #4675 registry is in-session ONLY: it is wiped on a full page reload and
 * never sees a server refetch (the persisted card content carries no `sent`
 * flag). So after a reload / conversation refetch / re-stream the card re-derived
 * with `sent=false` and re-exposed the Send button on an already-sent email — a
 * duplicate-send hazard. We additionally persist a per-card "sent" marker in
 * localStorage, keyed by the SAME stable cardKey the registry uses, so the SENT
 * view survives reload, refetch, re-stream, and re-mount. This mirrors
 * BulkDeleteConfirmCard's durable-resolution pattern. Scope is per-device
 * (localStorage), matching that accepted tradeoff; cross-device durability +
 * backend send-idempotency are a follow-up (#4982 part 2), not required for the
 * reported UI reproduction.
 */

/**
 * Session-scoped registry: cardKey → true once sent.
 * Module-level (singleton per page load) so it survives React tree
 * unmount/remount, but resets on full page navigation.
 */
const sentRegistry = new Map<string, true>();

/** localStorage key for a draft card's durable "sent" marker (#4982). Keyed on
 *  the same server-refetch-stable cardKey the registry uses, so the marker
 *  written on send matches what the renderer/card read back after a reload. */
export function emailDraftSentKey(cardKey: string): string {
  return `${EMAIL_SENT_MARKER_PREFIX}${cardKey}`;
}

/** Cap on durable sent markers (#5257/F-data-cache-11): one key per sent card
 *  otherwise grows without bound in the shared ~5MB localStorage quota. */
const SENT_MARKER_CAP = 200;

/** True if this draft card was already sent per the durable (localStorage)
 *  marker. Window-guarded so SSR/non-DOM contexts no-op; exported so the
 *  renderer can hydrate its first paint from the same source (#4982).
 *  Value-agnostic: legacy markers store '1', newer ones a write timestamp. */
export function readSent(cardKey: string): boolean {
  if (typeof window === 'undefined' || !cardKey) return false;
  try {
    return window.localStorage.getItem(emailDraftSentKey(cardKey)) !== null;
  } catch {
    return false;
  }
}

/** Drop the oldest markers once the cap is exceeded. Newer markers store their
 *  write time so age ordering is possible; legacy '1' values sort oldest. */
function pruneSentMarkers(): void {
  const markerKeys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(EMAIL_SENT_MARKER_PREFIX)) markerKeys.push(k);
  }
  if (markerKeys.length <= SENT_MARKER_CAP) return;
  markerKeys
    .sort((a, b) => (Number(window.localStorage.getItem(a)) || 0) - (Number(window.localStorage.getItem(b)) || 0))
    .slice(0, markerKeys.length - SENT_MARKER_CAP)
    .forEach((k) => window.localStorage.removeItem(k));
}

/** Record the durable "sent" marker so a reload/refetch keeps the SENT view. */
function writeSent(cardKey: string): void {
  if (typeof window === 'undefined' || !cardKey) return;
  try {
    window.localStorage.setItem(emailDraftSentKey(cardKey), String(Date.now()));
    pruneSentMarkers();
  } catch {
    /* storage full/blocked — non-fatal; the in-session registry still covers
       same-session re-mounts. */
  }
}

/** Test-only: clear the in-memory sent registry so a test can prove the DURABLE
 *  (localStorage) hydration path in isolation, without the in-session registry
 *  masking it. Mirrors useChatOnboarding's __resetOnboardingSessionsForTest. */
export function __resetSentMemoryForTests(): void {
  sentRegistry.clear();
}

export function EmailDraftCard({
  data,
  onSend,
  onCompose,
  cardKey,
  sent: sentProp = false,
  onSent,
  onEdit,
}: {
  data: EmailDraftCardData;
  onSend: (edited: { to: string; subject: string; body: string; cc?: string; bcc?: string }) => void | Promise<boolean | void>;
  onCompose?: (
    args: { style: 'casual' | 'professional'; rewriteFrom: string; avoidBody?: string },
  ) => Promise<{ subject?: string; body?: string } | null | void>;
  /** Stable identity key for the registry — derived by the parent from messageId+to+subject+threadId. */
  cardKey?: string;
  /** True if this card was already sent (from the session registry or parent state). */
  sent?: boolean;
  /** Called when the send succeeds so the parent can record it. */
  onSent?: (key: string) => void;
  /**
   * Called on every USER keystroke in a field with the full current field set,
   * so a hosting overlay can stash in-progress edits and make dismissal
   * lossless (escape-layer contract — never silently discard unsaved edits).
   * Machine-driven body updates (auto-draft / Rewrite) intentionally do NOT
   * fire this: only the user's own typing counts as unsaved work.
   */
  onEdit?: (fields: { to: string; subject: string; body: string; cc: string; bcc: string }) => void;
}) {
  // Resolve initial sent state: prop from parent, OR a fast in-session registry
  // hit, OR the durable (localStorage) marker that survives reload/refetch (#4982).
  const initialSent =
    sentProp || (cardKey ? sentRegistry.has(cardKey) || readSent(cardKey) : false);

  const [to, setTo] = useState(data.to ?? '');
  const [subject, setSubject] = useState(data.subject ?? '');
  const [body, setBody] = useState(data.body ?? '');
  const [cc, setCc] = useState(data.cc ?? '');
  const [bcc, setBcc] = useState(data.bcc ?? '');
  const [showCc, setShowCc] = useState(Boolean(data.cc));
  const [showBcc, setShowBcc] = useState(Boolean(data.bcc));
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent'>(
    initialSent ? 'sent' : 'idle',
  );
  // Which tone is currently being generated (drives the per-button "Writing…").
  const [rewritingStyle, setRewritingStyle] = useState<'casual' | 'professional' | null>(null);
  // Original rough note, captured at mount — every rewrite re-rewrites THIS.
  const [seed] = useState(data.body ?? '');
  // Tone of the draft currently shown, surfaced in the meta row.
  const [lastStyle, setLastStyle] = useState<'casual' | 'professional' | null>(null);
  // Rewrite alternates tone on each click (professional → casual → …); no tone
  // picker, just a single "Rewrite" the user can tap again for a different take.
  const [rewriteCount, setRewriteCount] = useState(0);

  const busy = sendState !== 'idle' || rewritingStyle !== null;
  // Reply mode (inbox reply) drafts via /draft-reply and needs no contactId;
  // compose mode (email_compose) needs a linked contact to voice the draft.
  const canRewrite = Boolean(onCompose && (data.contactId || data.replyMode));

  // Bug #4747 (placeholder flash): a blank reply card auto-drafts on mount, but
  // the effect fires AFTER the first render(s). Without a pending signal the
  // body would briefly show the "Type your reply…" prompt and invite the user
  // to type into a field the auto-draft is about to overwrite. We compute the
  // same auto-draft predicate the effect uses and seed a pending flag from mount
  // so the placeholder reflects the imminent draft until it lands or fails.
  const willAutoDraft =
    Boolean(onCompose && (data.contactId || data.replyMode)) &&
    (data.autoDraftReply || Boolean(data.replyMode)) &&
    !initialSent &&
    !(data.body ?? '').trim();
  const [autoDraftPending, setAutoDraftPending] = useState(willAutoDraft);

  // Run the underlying draft/compose request for a given tone and fold the
  // result into the card. Shared by the user "Rewrite" button and the on-mount
  // auto-pre-draft — it does NOT touch `rewriteCount`, so the auto-draft can
  // produce the initial draft without consuming the user's tone-toggle.
  // Returns true only when a draft actually landed, so the caller can advance
  // the tone-toggle solely on success (a failed compose must NOT shift tone).
  //
  // `isCancelled` lets a caller abandon an in-flight request: when it reports
  // true (e.g. the auto-draft effect's cleanup ran because the card unmounted)
  // every state setter no-ops, so we never call setState on an unmounted
  // component and a stale draft never folds into a card that is gone. The
  // user-driven Rewrite passes no predicate, so it is never treated as
  // cancelled.
  const runCompose = async (
    style: 'casual' | 'professional',
    isCancelled?: () => boolean,
  ): Promise<boolean> => {
    if (busy || !onCompose) return false;
    setRewritingStyle(style);
    try {
      const draft = await onCompose({
        style,
        rewriteFrom: seed,
        // Diverge from the current draft so re-running gives a fresh take.
        avoidBody: body && body !== seed ? body : undefined,
      });
      // Unmounted/torn down while the request was open — drop the result without
      // touching state. `finally` still clears `rewritingStyle` below, also guarded.
      if (isCancelled?.()) return false;
      if (draft) {
        if (typeof draft.subject === 'string') setSubject(draft.subject);
        if (typeof draft.body === 'string') setBody(draft.body);
        setLastStyle(style);
        // A fresh rewrite supersedes any prior "sent" lock.
        setSendState('idle');
        return true;
      }
      // Null/empty result — no draft landed; treat as a failed compose.
      return false;
    } catch {
      // Leave fields untouched on failure; the parent surfaces a toast.
      return false;
    } finally {
      // Skip the spinner reset too when cancelled — the component is gone.
      if (!isCancelled?.()) setRewritingStyle(null);
    }
  };

  const handleRewrite = async () => {
    if (busy || !onCompose) return;
    // Alternate tone each EXPLICIT user click: professional, casual, professional, …
    const style: 'casual' | 'professional' = rewriteCount % 2 === 0 ? 'professional' : 'casual';
    // Advance the tone-toggle only on a SUCCESSFUL explicit user rewrite, so a
    // failed compose retries the SAME tone next time (no permanent de-sync), and
    // the on-mount auto-pre-draft (which ignores this return value) never shifts
    // the alternation.
    const ok = await runCompose(style);
    if (ok) setRewriteCount((n) => n + 1);
  };

  // Bug #4747: a REPLY card lands blank (the tool emits no body), so auto-fire
  // the existing draft-reply path ONCE on mount instead of forcing the user to
  // click Rewrite first. The agent's email_compose tool sets `autoDraftReply`
  // on reply cards with no seeded body; we also fall back to (replyMode + empty
  // body) so any blank reply card pre-drafts even from paths that don't set the
  // flag. Gated on an empty body + idle state so we never clobber a user note,
  // a pre-seeded reply (e.g. InboxResultCard's cached aiDraft), or an
  // already-sent card. A ref makes React Strict-Mode's double-invoke and
  // subsequent re-renders fire it at most once — but a run that was CANCELLED
  // (cleanup ran before it landed) re-arms the ref so the next mount re-fires;
  // otherwise Strict-Mode's mount→cleanup→remount would leave the card blank.
  const shouldAutoDraftReply = data.autoDraftReply || Boolean(data.replyMode);
  const autoDraftFiredRef = useRef(false);
  useEffect(() => {
    if (autoDraftFiredRef.current) return;
    if (!canRewrite || !shouldAutoDraftReply || sendState !== 'idle' || body.trim()) {
      // Conditions don't hold — there is no imminent auto-draft, so clear the
      // pending placeholder so the normal prompt shows.
      setAutoDraftPending(false);
      return;
    }
    autoDraftFiredRef.current = true;
    // The compose request outlives a synchronous unmount (it's an async fetch
    // the parent owns). If the card unmounts before it resolves — the message
    // scrolls out, the conversation switches, Strict-Mode tears the first mount
    // down — we must NOT setState on the gone component or fold a stale draft
    // into it. `cancelled` is flipped by this effect's cleanup; runCompose reads
    // it (via the predicate) before every setter, and we guard the pending-clear
    // too. There is no AbortController because `onCompose` exposes no
    // cancellation signal; this no-ops the result instead of aborting the wire
    // request (the in-flight fetch is harmless once its result is dropped).
    let cancelled = false;
    // Draft the initial reply at the same starting tone a first user Rewrite
    // would pick ('professional'), but via runCompose directly so it does NOT
    // increment rewriteCount — the first USER Rewrite click then still yields
    // the intended next tone instead of being skipped past it. The return value
    // is intentionally ignored: the auto-draft never consumes the tone-toggle.
    // Clear the pending placeholder once it resolves (success OR failure) so the
    // field stops showing the drafting state — but only while still mounted.
    void runCompose('professional', () => cancelled).finally(() => {
      if (!cancelled) setAutoDraftPending(false);
    });
    return () => {
      cancelled = true;
      // Re-arm so a subsequent mount (Strict-Mode double-invoke, or a real
      // remount after this run was abandoned) can fire a fresh, uncancelled
      // request — the cancelled run above will never populate the body.
      autoDraftFiredRef.current = false;
    };
    // Mount-only: deps intentionally omitted so this fires once for the reply
    // card and never re-triggers on subsequent body/state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send is allowed once there's a recipient AND something to deliver — either a
  // non-empty body OR an attached doc (a doc-email can legitimately go out with an
  // empty body, the PDF is the payload). Without the attachment branch the Send
  // button stayed disabled on an "email my <doc>" card whose body the user cleared
  // (Bug #4). Mirrors the button's `disabled` predicate below — keep them in sync.
  const canSend = Boolean(to.trim()) && (Boolean(body.trim()) || Boolean(data.attachDocId));

  const handleSend = async () => {
    // Guard against double-send: a real email is dispatched, so a rapid second
    // click (or a click after success) must be a no-op.
    if (busy || !canSend) return;
    setSendState('sending');
    try {
      const ok = await onSend({
        to,
        subject,
        body,
        ...(cc.trim() ? { cc: cc.trim() } : {}),
        ...(bcc.trim() ? { bcc: bcc.trim() } : {}),
      });
      if (ok === false) {
        setSendState('idle');
      } else {
        setSendState('sent');
        // Record sent-state in BOTH the fast in-session registry (covers re-mounts)
        // AND the durable localStorage marker (survives reload/refetch/re-stream,
        // #4982) so the card can never revert to a sendable state on this device.
        if (cardKey) {
          sentRegistry.set(cardKey, true);
          writeSent(cardKey);
          onSent?.(cardKey);
        }
      }
    } catch {
      setSendState('idle');
    }
  };

  // Once sent, the card flips to a confirmation view so the editable form (and
  // its Send button) can't reappear on an already-sent email.
  if (sendState === 'sent') {
    return (
      <div className="rc-card rc-email-sent">
        <div className="rc-meta">
          <span className="rc-meta-kind rc-sent-kind">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            SENT
          </span>
          <span className="rc-meta-sep">·</span>
          <span className="rc-meta-match">TO {(data.toName || to).toUpperCase()}</span>
        </div>
        {subject && <div className="rc-sent-subject">{subject}</div>}
        {body && <div className="rc-sent-snippet">{body}</div>}
        {/* Task B3: keep the attachment visible on the sent confirmation. */}
        {data.attachDocId && (
          <div className="rc-attachment-chip" title={`${data.attachDocTitle ?? 'Document'}.pdf`}>
            📎 {data.attachDocTitle ? `${data.attachDocTitle}.pdf` : 'Document.pdf'}
          </div>
        )}
        <div className="rc-sent-foot">Delivered to {to}</div>
      </div>
    );
  }

  const addCcBtn = (
    <button
      type="button"
      className="rc-cc-bcc-btn"
      onClick={() => setShowCc(true)}
    >
      Add Cc
    </button>
  );
  const addBccBtn = (
    <button
      type="button"
      className="rc-cc-bcc-btn"
      onClick={() => setShowBcc(true)}
    >
      Add Bcc
    </button>
  );

  return (
    <div className="rc-card rc-email-draft">
      <div className="rc-meta">
        <span className="rc-meta-kind">{data.replyMode ? '↩️ REPLY' : '✉️ EMAIL'}</span>
        {data.toName && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-match">TO {data.toName.toUpperCase()}</span>
          </>
        )}
        {lastStyle && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-match">{lastStyle.toUpperCase()}</span>
          </>
        )}
      </div>

      {/* Task B3: doc-as-PDF attachment chip. Rendered from data.attachDocId, so
          it is INDEPENDENT of the body text and survives the Rewrite/voice compose
          (which only rewrites subject/body). The doc's text is never inlined —
          this chip is the sole surface of the attachment on the card. */}
      {data.attachDocId && (
        <div className="rc-attachment-chip" title={`${data.attachDocTitle ?? 'Document'}.pdf`}>
          📎 {data.attachDocTitle ? `${data.attachDocTitle}.pdf` : 'Document.pdf'}
        </div>
      )}

      <label className="rc-field">
        <span className="rc-field-label">To</span>
        <input
          className="rc-input"
          type="text"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            onEdit?.({ to: e.target.value, subject, body, cc, bcc });
          }}
          placeholder="recipient@example.com"
        />
      </label>

      {/* Cc slot — the field once added; otherwise, ONLY once Bcc is showing, the
          Add-Cc pill sits here so Cc always stays ABOVE Bcc. When both are still
          hidden the two pills pair up inline in the row below (Cc left). */}
      {showCc ? (
        <label className="rc-field">
          <span className="rc-field-label">Cc</span>
          <input
            className="rc-input"
            type="text"
            value={cc}
            onChange={(e) => {
              setCc(e.target.value);
              onEdit?.({ to, subject, body, cc: e.target.value, bcc });
            }}
            placeholder="cc@example.com"
          />
        </label>
      ) : showBcc ? (
        <div className="rc-cc-bcc-toggles">{addCcBtn}</div>
      ) : null}

      {showBcc ? (
        <label className="rc-field">
          <span className="rc-field-label">Bcc</span>
          <input
            className="rc-input"
            type="text"
            value={bcc}
            onChange={(e) => {
              setBcc(e.target.value);
              onEdit?.({ to, subject, body, cc, bcc: e.target.value });
            }}
            placeholder="bcc@example.com"
          />
        </label>
      ) : null}

      {/* Pills below the fields: both hidden → [Add Cc] [Add Bcc] inline (Cc left);
          Cc added but not Bcc → just [Add Bcc]. (When only Bcc shows, Add Cc is
          rendered in the Cc slot above instead.) */}
      {!showBcc && (
        <div className="rc-cc-bcc-toggles">
          {!showCc && addCcBtn}
          {addBccBtn}
        </div>
      )}

      <label className="rc-field">
        <span className="rc-field-label">Subject</span>
        <input
          className="rc-input"
          type="text"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            onEdit?.({ to, subject: e.target.value, body, cc, bcc });
          }}
          placeholder="Subject"
        />
      </label>

      <label className="rc-field">
        <span className="rc-field-label">Body</span>
        <textarea
          className="rc-textarea"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            onEdit?.({ to, subject, body: e.target.value, cc, bcc });
          }}
          rows={5}
          placeholder={
            data.replyMode
              ? // Show the drafting state while the on-mount auto-draft is still
                // pending OR a rewrite is in flight, so the user is never invited
                // to type into a field the auto-draft is about to overwrite.
                rewritingStyle || autoDraftPending
                ? 'Drafting your reply…'
                : 'Type your reply, or hit Rewrite for another take…'
              : canRewrite
                ? 'Jot a rough note, then hit Rewrite…'
                : 'Write your message…'
          }
        />
      </label>

      <div className="rc-actions rc-actions--draft">
        {canRewrite && (
          <button
            type="button"
            className="rc-btn-draft"
            onClick={handleRewrite}
            disabled={busy}
          >
            {rewritingStyle ? 'Writing…' : 'Rewrite'}
          </button>
        )}
        <button
          type="button"
          className="rc-btn-draft"
          onClick={handleSend}
          disabled={busy || !canSend}
        >
          {/* sendState 'sent' early-returns the confirmation view above, so only
              'sending' / 'idle' reach this button. */}
          {sendState === 'sending' ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
